import type { RecordView } from '@kit/sync/types/record-view';
import { RotateCcw } from 'lucide-react';
import { PriorityBadge } from '@/entities/shared/priority/priority-badge';
import { RecordTitle } from '@/entities/shared/record/record-title';
import { patchTask } from '@/entities/task/api/patch-task';
import { TaskDoneButton } from '@/entities/task/components/task-done-button';
import { TaskDue } from '@/entities/task/components/task-due';
import { REPEAT_UNIT_LABELS } from '@/entities/task/config/repeat-unit-labels';
import { TASK_LABELS } from '@/entities/task/config/task-labels';
import { isOpenTask } from '@/entities/task/helpers/is-open-task';
import type { Task } from '@/entities/task/schemas/task';
import { ListRow } from '@/shared/components/list/list-row';
import { useTranslation } from '@/shared/i18n/use-translation';
import { formatDate } from '@/shared/lib/dates/format-date';
import { Button } from '@/shared/ui/button/button';

type TaskRowProps = {
  readonly view: RecordView<Task>;
  readonly onOpen: () => void;
};

export function TaskRow({ view, onOpen }: TaskRowProps) {
  const { t, tn } = useTranslation();
  const task = view.data;
  const open = isOpenTask(task);
  const every = task.repeat_every;
  const repeat = REPEAT_UNIT_LABELS[task.repeat_unit ?? 'months'];
  const steps = task.checklist ?? [];
  const stepsDone = steps.filter((step) => step.done).length;
  const subtitle = [
    t(TASK_LABELS.kind[task.kind]),
    every ? tn(every, repeat.one, repeat.other) : null,
    open && steps.length > 0 ? `${stepsDone}/${steps.length}` : null,
    open && task.status === 'doing' ? t('in progress') : null,
    open ? null : t(TASK_LABELS.status[task.status]),
    !open && task.done_on ? formatDate(task.done_on) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <ListRow
      onClick={onOpen}
      muted={!open}
      leading={
        open ? (
          <TaskDoneButton view={view} />
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full"
            aria-label={t('Reopen')}
            onClick={() => void patchTask(view.id, { status: 'todo', done_on: null })}
          >
            <RotateCcw />
          </Button>
        )
      }
      title={
        <RecordTitle pending={view.pending} struck={!open}>
          {task.title}
        </RecordTitle>
      }
      subtitle={subtitle}
      status={
        open ? (
          <>
            <PriorityBadge priority={task.priority} />
            <TaskDue task={task} />
          </>
        ) : undefined
      }
    />
  );
}
